import { Link } from 'react-router-dom';
import { PublicFooter, PublicHeader } from '@/components/public/PublicLayout';
import TermsSummary from '../components/legal/TermsSummary';

/*
 * Platform-wide Terms and Conditions, linked from registration, the landing page
 * footer and the Privacy Policy. Module terms (the Spray Module T&C, served
 * from spray_tnc_versions and accepted in-app) sit on top of these and win
 * where the two conflict — see "Additional terms" below.
 *
 * Bump EFFECTIVE_DATE and VERSION on every substantive change, and publish
 * the same version string server-side (a row in platform_terms_versions, see
 * HHNodeServer migration 20260929-07) — that row is what makes the in-app
 * gate (PlatformTermsGate) ask everyone to accept again. Fill in
 * LEGAL_ENTITY / ABN with the contracting company before relying on these in
 * production: "FactreeTech" is the trading name, not a party that can contract.
 */
const EFFECTIVE_DATE = '30 September 2026';
const VERSION = '2026.09.2';
const COMPANY = 'FactreeTech';
const LEGAL_ENTITY = 'FactreeTech';
const ABN = '';
const CONTACT_EMAIL = 'support@factree.com.au';
const WEB_URL = 'https://tech.factree.com.au';

function Section({ id, title, children }: { id: string; title: string; children: React.ReactNode }) {
  return (
    <section id={id} className="scroll-mt-24 space-y-3">
      <h2 className="text-2xl font-semibold text-neutral-900">{title}</h2>
      <div className="space-y-3 text-neutral-700 leading-relaxed">{children}</div>
    </section>
  );
}

function Bullets({ children }: { children: React.ReactNode }) {
  return <ul className="list-disc pl-5 space-y-2">{children}</ul>;
}

const CONTENTS: [string, string][] = [
  ['about', 'About these Terms'],
  ['definitions', 'Definitions'],
  ['platform', 'What the Platform is — and is not'],
  ['accounts', 'Accounts, users and seats'],
  ['fees', 'Subscriptions, trials, fees and invoices'],
  ['no-advice', 'No professional advice'],
  ['your-content', 'Your Content and your data'],
  ['shared-content', 'Shared, public and third-party information'],
  ['cultivars', 'Cultivars, Plant Breeder’s Rights and intellectual property'],
  ['trade', 'Trees, plant material and sales'],
  ['module-terms', 'Module terms, and chemicals'],
  ['irrigation', 'Irrigation, hardware and remote control'],
  ['forecasts', 'Weather, sensors, forecasts and pest models'],
  ['workforce', 'Timesheets, payroll, leave and workforce'],
  ['whs', 'Work health and safety, and ISO 45001'],
  ['maps', 'Maps, boundaries, areas and land data'],
  ['equipment', 'Equipment, maintenance and calibration'],
  ['automated', 'AI-assisted and automated features'],
  ['integrations', 'Third-party services and integrations'],
  ['acceptable-use', 'Acceptable use'],
  ['privacy', 'Privacy and personal information'],
  ['availability', 'Availability, changes and updates'],
  ['export', 'Data export, retention and deletion'],
  ['our-ip', 'Our intellectual property and feedback'],
  ['warranties', 'Warranties and the Australian Consumer Law'],
  ['liability', 'Limitation of liability'],
  ['indemnity', 'Indemnity'],
  ['termination', 'Suspension and termination'],
  ['changes', 'Changes to these Terms'],
  ['disputes', 'Disputes and governing law'],
  ['general', 'General'],
  ['contact', 'Contact us'],
];

function TermsAndConditions() {
  const provider = ABN ? `${LEGAL_ENTITY} (ABN ${ABN})` : LEGAL_ENTITY;

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <PublicHeader />

      {/* Body */}
      <main className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-10 space-y-10">
        <div className="space-y-2">
          <h1 className="text-4xl font-bold tracking-tight text-neutral-900">Terms and Conditions</h1>
          <p className="text-sm text-neutral-500">
            Effective date: {EFFECTIVE_DATE} &middot; Version {VERSION}
          </p>
          <p className="text-neutral-700 leading-relaxed">
            These Terms and Conditions (&ldquo;Terms&rdquo;) govern access to and use of the {COMPANY} web
            application at <a href={WEB_URL} className="text-primary-600 hover:underline">{WEB_URL}</a>, the{' '}
            {COMPANY} mobile apps, connected hardware and firmware, application programming interfaces and
            related services (together, the &ldquo;Platform&rdquo;), provided by {provider} (&ldquo;{COMPANY}&rdquo;,
            &ldquo;we&rdquo;, &ldquo;us&rdquo;, &ldquo;our&rdquo;).
          </p>
        </div>

        <TermsSummary />

        {/* Contents */}
        <nav aria-label="Contents" className="rounded-lg border border-neutral-200 bg-neutral-50 p-4">
          <p className="text-sm font-semibold text-neutral-900 mb-2">Contents</p>
          <ol className="list-decimal pl-5 text-sm text-primary-700 space-y-1">
            {CONTENTS.map(([id, title]) => (
              <li key={id}><a href={`#${id}`} className="hover:underline">{title}</a></li>
            ))}
          </ol>
        </nav>

        <Section id="about" title="1. About these Terms">
          <p>
            1.1 These Terms are accepted for each Organisation by one of its Organisation Admins, who confirms
            that they are authorised to bind it. That acceptance covers the Organisation and everyone who uses
            the Platform under its account. By creating an account, clicking &ldquo;I agree&rdquo;, or accessing or
            using the Platform, each User also agrees to use it in accordance with these Terms. If you do not
            agree, you must not use the Platform.
          </p>
          <p>
            1.2 If you use the Platform on behalf of a business, farm, nursery, contractor or other entity,
            &ldquo;you&rdquo; includes that entity. Where you use the Platform as an employee, contractor or member of
            an Organisation, you must also comply with that Organisation&rsquo;s reasonable directions about its use,
            and the Organisation is responsible for making its Users aware of these Terms.
          </p>
          <p>
            1.3 The Platform is intended for business use. It is not designed for, or offered to, consumers
            acquiring services for personal, domestic or household purposes.
          </p>
          <p>
            1.4 These Terms should be read with our{' '}
            <Link to="/privacy" className="text-primary-600 hover:underline">Privacy Policy</Link>, any
            additional terms for a particular module (see clause 11 and clause 31), Tree Sales Terms for any
            purchase of plant material (see clause 10), and any order form,
            quote, offer or written billing terms agreed with your Organisation.
          </p>
        </Section>

        <Section id="definitions" title="2. Definitions">
          <Bullets>
            <li><strong>Organisation</strong> — the business or entity that holds a {COMPANY} account and subscription, and on whose behalf Users access the Platform.</li>
            <li><strong>User</strong> — any individual who accesses the Platform, including under an Organisation&rsquo;s account, and including an individual recorded on a roster, crew or picker list without their own login.</li>
            <li><strong>Organisation Admin</strong> — a User the Organisation has given administrative control of its account, users, seats or settings.</li>
            <li><strong>Module</strong> — a functional area of the Platform, such as Timesheets &amp; HR, Spray, Irrigation, Farms, Cultivar Registry, Nursery, Farm Jobs, Equipment and Weather.</li>
            <li><strong>Your Content</strong> — all data, records, text, images, files, boundaries, readings and other material entered, uploaded, generated from your devices, or imported into the Platform by or for your Organisation or its Users.</li>
            <li><strong>Shared Content</strong> — Your Content that an Organisation chooses to make visible to other Organisations or the public through the Platform (for example, a published cultivar record, an evaluation reading, a nursery offer or a verification certificate).</li>
            <li><strong>Reference Data</strong> — information we make available from public or third-party sources, or that we curate ourselves, such as chemical registrations, label extracts, weather data, growing regions, pest and disease models, system catalogues and default settings.</li>
            <li><strong>Fees</strong> — the amounts payable for a subscription, seats, add-ons or services.</li>
          </Bullets>
        </Section>

        <Section id="platform" title="3. What the Platform is — and is not">
          <p>
            3.1 The Platform is a tool for recording, organising, planning, monitoring and reporting on farm,
            orchard, nursery and workforce operations. It gives each Organisation a place to build up its own
            knowledge — its farms, blocks, cultivars, observations, records, programs and procedures — and
            to use that knowledge in its day-to-day work.
          </p>
          <p>
            3.2 Each Organisation decides what it records, how it configures the Platform, what it relies on
            and what it does in the real world. We provide the software. We do not farm, grow, propagate,
            sell trees on your behalf, spray, irrigate, employ your staff, or supervise any activity recorded
            on the Platform.
          </p>
          <p>
            3.3 We do not review, verify, audit, endorse or take responsibility for Your Content or any other
            Organisation&rsquo;s content. The presence of a record, calculation, recommendation, warning or
            the absence of a warning on the Platform does not mean that anything is accurate, lawful, safe,
            suitable or complete.
          </p>
          <p>
            3.4 The Platform supports, but does not replace, your own judgement, professional advice,
            physical inspection, equipment manufacturer instructions, product labels and legal obligations.
          </p>
        </Section>

        <Section id="accounts" title="4. Accounts, users and seats">
          <p>4.1 You must provide accurate registration information and keep it up to date.</p>
          <p>
            4.2 You are responsible for keeping your login credentials, passkeys and multi-factor devices
            secure, and for all activity under your account. Tell us promptly at{' '}
            <a href={`mailto:${CONTACT_EMAIL}`} className="text-primary-600 hover:underline">{CONTACT_EMAIL}</a>{' '}
            if you suspect unauthorised access.
          </p>
          <p>4.3 Each Organisation is responsible for:</p>
          <Bullets>
            <li>who it invites, which roles and licence seats it assigns, and removing access when someone leaves;</li>
            <li>the acts and omissions of its Users, as if they were its own;</li>
            <li>information it records about individuals who do not have their own login, such as roster workers, pickers, crew members, customers and contacts; and</li>
            <li>ensuring its Users are trained, qualified and authorised for the work they record or control through the Platform.</li>
          </Bullets>
          <p>
            4.4 Licence seats are assigned to individuals and may not be shared. Administrative or platform
            roles do not grant access to a Module without a seat for that Module.
          </p>
          <p>
            4.5 A User who belongs to more than one Organisation must keep each Organisation&rsquo;s
            information separate and use it only for that Organisation&rsquo;s purposes.
          </p>
        </Section>

        <Section id="fees" title="5. Subscriptions, trials, fees and invoices">
          <p>
            5.1 Fees are as shown in the Platform at the time of purchase, or as set out in an order form,
            quote or written offer agreed with us. Unless stated otherwise, Fees are in Australian dollars
            and exclusive of GST, which is added where applicable.
          </p>
          <p>
            5.2 Subscriptions renew automatically at the end of each billing period until cancelled. Seats
            added part-way through a period may be charged pro rata to the next billing date.
          </p>
          <p>
            5.3 A trial gives access for the period stated. Unless it is cancelled before the trial ends,
            it converts to a paid subscription and is invoiced from the end of the trial. We tell
            Organisation Admins before a trial converts.
          </p>
          <p>
            5.4 Invoices are payable within the time stated on the invoice. If an amount is overdue, we may,
            after giving reasonable notice, suspend access to the relevant Modules until it is paid.
            Suspension does not delete Your Content.
          </p>
          <p>
            5.5 You may cancel a subscription or remove seats at any time, effective at the end of the
            current billing period. Except where these Terms or the law say otherwise, Fees already paid are
            not refundable.
          </p>
          <p>
            5.6 We may change our prices by giving at least 30 days&rsquo; notice to Organisation Admins.
            New prices apply from your next billing period after the notice ends. If you do not accept the
            change, you may cancel before it takes effect.
          </p>
        </Section>

        <Section id="no-advice" title="6. No professional advice">
          <p>
            6.1 <strong>We do not provide professional advice of any kind.</strong> Nothing on the Platform
            — including any calculation, estimate, forecast, suggestion, default value, alert, warning,
            risk rating, template, report or AI-generated content — is agronomic, horticultural,
            entomological, plant-pathology, chemical, irrigation-engineering, hydraulic, soil, nutrition,
            veterinary, biosecurity, surveying, legal, intellectual-property, employment, industrial
            relations, payroll, accounting, tax, financial or work health and safety advice.
          </p>
          <p>
            6.2 Features such as tree-count calculations, harvest-window suggestions, maturity estimates,
            withholding-period checks, pest and disease risk models, flow and pressure calculations, leave
            accruals and labour costings are aids to your own decision-making. They rely on inputs,
            assumptions and data that may be incomplete, out of date or wrong.
          </p>
          <p>
            6.3 You are responsible for obtaining advice from an appropriately qualified and, where required,
            licensed professional (for example an agronomist, chemical reseller, irrigation designer,
            surveyor, lawyer, accountant or payroll specialist) before acting on anything in the Platform
            where the consequences matter.
          </p>
          <p>
            6.4 Where an Organisation or its adviser enters advice, recommendations or programs into the
            Platform, that advice is theirs, not ours.
          </p>
          <p>
            6.5 Compliance checklists, packs and automated checks summarise an Organisation&rsquo;s own records
            against topics drawn from certification and assurance schemes such as GlobalG.A.P. and Freshcare.
            They are written by us, are not the schemes&rsquo; official checklists and are not endorsed by the
            scheme owners. A &ldquo;met&rdquo; result means only that the records held in the Platform show
            what the check describes; it does not mean the Organisation complies with a scheme, the law or a
            label, or that it will be certified. Certification decisions are made by the certifying body.
          </p>
        </Section>

        <Section id="your-content" title="7. Your Content and your data">
          <p>
            7.1 <strong>You own Your Content.</strong> As between you and us, your Organisation retains all
            rights in Your Content.
          </p>
          <p>
            7.2 You grant us a non-exclusive, worldwide, royalty-free licence to host, store, copy, process,
            transmit, display and adapt Your Content to the extent necessary to provide, secure, support and
            improve the Platform for you, and to comply with law.
          </p>
          <p>
            7.3 We may create and use aggregated or de-identified information derived from use of the
            Platform (for example, usage statistics or regional seasonal trends) to operate and improve the
            Platform, provided it does not identify your Organisation, any individual, or your
            commercially sensitive information. We will not sell Your Content.
          </p>
          <p>7.4 You are solely responsible for Your Content, and you warrant that:</p>
          <Bullets>
            <li>you have all rights, licences, consents and authority needed to enter it and to let us use it under these Terms;</li>
            <li>it is, to the best of your knowledge, accurate and not misleading — particularly records that others may rely on, such as spray, chemical-use, harvest, traceability, plant-health, timesheet and payroll records;</li>
            <li>it does not infringe anyone&rsquo;s intellectual property, confidentiality, contractual or privacy rights; and</li>
            <li>entering it does not breach any law or any agreement you are bound by, including a licence, sublicence, supply, growing, confidentiality or exclusivity agreement.</li>
          </Bullets>
          <p>
            7.5 We may remove, restrict or disable access to content that we reasonably believe breaches
            these Terms, infringes someone&rsquo;s rights or is unlawful, and will tell the affected
            Organisation where it is lawful and practical to do so.
          </p>
        </Section>

        <Section id="shared-content" title="8. Shared, public and third-party information">
          <p>
            8.1 Some Platform features let an Organisation share information with other Organisations or
            publish it — for example cultivar records, evaluation readings, maturity observations, nursery
            offers, licence and propagator terms, and certificate verification pages. When you share
            content, you grant those Organisations and Users the right to view and use it for the purpose
            the feature is designed for, and you are responsible for deciding what to share.
          </p>
          <p>
            8.2 Content shared by other Organisations is their responsibility. We do not verify it and are
            not liable for it or for any reliance you place on it.
          </p>
          <p>
            8.3 Reference Data comes from sources such as the APVMA, IP Australia, UPOV, plant-variety
            authorities in other countries, the Bureau of Meteorology, Open-Meteo, state land and planning
            registers, manufacturers&rsquo; published documents and our own curation. It may be incomplete,
            out of date, misread or wrong. It is provided for convenience only and you must verify it against
            the authoritative source before relying on it.
          </p>
          <p>
            8.4 Links to third-party websites and documents are provided for convenience. We are not
            responsible for their content, availability or accuracy.
          </p>
          <p>
            8.5 An Organisation can give an auditor, certifier or other reviewer it chooses read-only access
            to selected records (for example spray, chemical, training, induction, safety and harvest
            records) for a period it sets, or export those records as a compliance pack. The reviewer does
            not become a User: they sign in with a link and a one-time code sent to the email address the
            Organisation gives, access ends when the period ends or when the Organisation revokes it, and each
            record the reviewer opens is logged and shown to the Organisation. The Organisation decides what
            is shared and with whom, is responsible for having authority to disclose the personal
            information in those records and for any notice to the people concerned (clause 21.2), and its
            arrangements with the reviewer govern what the reviewer may do with what they see.
          </p>
        </Section>

        <Section id="cultivars" title="9. Cultivars, Plant Breeder’s Rights and intellectual property">
          <p>
            9.1 The Platform lets Organisations record cultivars, varieties, rootstocks, selections, trade
            names, breeding material, protection details, licences, sublicences, royalties, propagation
            permissions and related agreements. <strong>These are Your Content.</strong> We do not create,
            grant, verify, enforce or administer any of these rights.
          </p>
          <p>9.2 Each Organisation is solely responsible for ensuring that:</p>
          <Bullets>
            <li>any cultivar or plant material it records, propagates, grows, sells, offers, evaluates or distributes is used in accordance with all applicable Plant Breeder&rsquo;s Rights, plant patents, trade marks and other intellectual property rights, in Australia and anywhere else it deals with that material;</li>
            <li>it complies with every licence, sublicence, growing agreement, club or managed-variety arrangement, exclusivity, territory, quantity, confidentiality and royalty obligation that applies to it; and</li>
            <li>names, descriptions, images and characteristics it records or publishes do not infringe or misrepresent anyone&rsquo;s rights.</li>
          </Bullets>
          <p>
            9.3 The appearance of a cultivar on the Platform — in any Organisation&rsquo;s registry, in a
            shared or global list, in a nursery offer, or in a result from a Plant Breeder&rsquo;s Rights or
            patent lookup — does not mean that it is unprotected, that it is lawfully available, that anyone
            is authorised to propagate or sell it, or that any protection information shown is correct or
            current. Protection status, expiry dates and ownership change and must be confirmed with the
            relevant authority and the rights holder.
          </p>
          <p>
            9.4 Licence, sublicence and propagator-terms features are record-keeping and workflow tools. Any
            agreement they record or generate is between the parties to it. Unless we are ourselves a party to
            it (for example as seller or licensor under clause 10), we are not a party to that agreement, we do
            not act as agent for either party, and we do not guarantee that it is
            enforceable, complete, or suitable for your circumstances. You should obtain legal advice on any
            agreement that matters.
          </p>
          <p>
            9.5 Where you buy plant material from or through us, the rights and obligations attached to that
            cultivar are set out in clause 10 and the Tree Sales Terms, not in this clause.
          </p>
          <p>
            9.6 We are not liable for any claim, loss or dispute arising from an Organisation&rsquo;s
            infringement of, or failure to honour, any intellectual property right or contractual obligation
            relating to plant material, whether or not that material or agreement is recorded on the
            Platform.
          </p>
          <p>
            9.7 <strong>If you believe content on the Platform infringes your rights</strong>, email{' '}
            <a href={`mailto:${CONTACT_EMAIL}`} className="text-primary-600 hover:underline">{CONTACT_EMAIL}</a>{' '}
            with the details of your right, the content concerned and your contact details. We will review
            the notice and may remove or restrict access to content, notify the Organisation responsible, or
            refer you to them. We are not required to decide disputes between Organisations and rights
            holders.
          </p>
        </Section>

        <Section id="trade" title="10. Trees, plant material and sales">
          <p>
            10.1 <strong>Tree sales have their own terms.</strong> Trees, budwood, rootstocks and other plant
            material may be ordered on or through the Platform in more than one way, described below. Every such
            sale is governed by the terms and conditions of sale that apply to it (&ldquo;Tree Sales
            Terms&rdquo;), which are provided with the quote, offer, order or order confirmation. These Terms do
            not govern the sale itself, and if they are inconsistent with the Tree Sales Terms, the Tree Sales
            Terms prevail for that sale.
          </p>
          <p>10.2 Plant material may be supplied:</p>
          <Bullets>
            <li><strong>by {COMPANY} (or a related entity) as seller</strong> — including cultivars we hold rights to or are licensed to supply, whether grown by us or by a nursery growing them for us under contract;</li>
            <li><strong>through {COMPANY} as agent</strong> — where we arrange a sale on behalf of a nursery or other supplier; or</li>
            <li><strong>directly by another Organisation</strong> — where a nursery or supplier uses the Platform to sell to its own customers on its own account.</li>
          </Bullets>
          <p>
            10.3 The quote, offer or order confirmation will identify who the seller is. Where we act as agent, the
            contract of supply is between you and the named nursery or supplier unless the Tree Sales Terms say
            otherwise. A nursery growing plant material for us under contract is our supplier, not the seller to
            you, unless the Tree Sales Terms say otherwise.
          </p>
          <p>
            10.4 <strong>Plant Breeder&rsquo;s Rights, licences and non-propagation.</strong> Many cultivars are
            protected by Plant Breeder&rsquo;s Rights, patents, trade marks or licence arrangements, and are
            supplied only on the conditions the rights holder imposes. Depending on the cultivar, those conditions
            may require the purchaser to:
          </p>
          <Bullets>
            <li>sign a non-propagation agreement, grower licence, sublicence or similar agreement before or on delivery;</li>
            <li>not propagate, graft, bud, top-work, take cuttings or budwood from, or otherwise reproduce the plant material, or allow anyone else to, except as the rights holder permits in writing;</li>
            <li>not sell, give or transfer plant material or propagating material to anyone else;</li>
            <li>pay royalties or levies (for example per tree, per hectare or per carton);</li>
            <li>plant only in approved locations or quantities, keep records of plantings, and allow the rights holder or its licensee to inspect or verify them;</li>
            <li>comply with the rules of any club or managed-variety program, including restrictions on how the fruit is packed, branded and marketed; and</li>
            <li>pass these obligations on to anyone who later acquires the orchard or the trees.</li>
          </Bullets>
          <p>
            These obligations are set out in the Tree Sales Terms and any licence or agreement for the cultivar,
            and continue after delivery. Breaching them can infringe the rights holder&rsquo;s rights and expose
            the purchaser to claims by the rights holder as well as by us. Where the Tree Sales Terms or licence
            require it, planting records you keep on the Platform for that cultivar may be used to confirm
            compliance, and shared with the rights holder or its licensee for that purpose.
          </p>
          <p>
            10.5 <strong>Sales between other Organisations.</strong> Where an Organisation sells to its own
            customers on its own account (clause 10.2, third point), the transaction is solely between the parties
            to it. {COMPANY} is not the seller, buyer, agent or guarantor of that sale and is not a party to the
            contract. For those sales we are not responsible for:
          </p>
          <Bullets>
            <li>the quality, variety, trueness-to-type, health, age, grade, size, rootstock, viability, quantity or suitability of any tree, plant, budwood, seed or other material;</li>
            <li>plant health, biosecurity, quarantine, interstate movement, accreditation or certification requirements;</li>
            <li>availability, pricing, delivery, despatch, freight, collection, substitution, backorders, returns, refunds or warranties;</li>
            <li>payment, credit, invoicing, GST or debt recovery; or</li>
            <li>the terms of trade, representations or conduct of the seller or buyer, including whether the seller is entitled to sell a protected cultivar.</li>
          </Bullets>
          <p>
            10.6 Each Organisation that sells through the Platform on its own account is responsible for its own
            terms of trade, for any licence it needs to sell a protected cultivar, for complying with the
            Australian Consumer Law and other law in its dealings with its customers, and for resolving any
            complaint or dispute with them.
          </p>
          <p>
            10.7 <strong>Payments taken through the Platform.</strong> Where a buyer pays for another
            Organisation&rsquo;s sale through a payment facility on the Platform (for example card checkout on a
            nursery&rsquo;s website orders), we receive that payment only as the seller&rsquo;s limited agent for
            collecting it, and remit it to the seller. Payment to us discharges the buyer&rsquo;s obligation to
            pay the seller that amount. Receiving the payment does not make us the seller or a party to the sale,
            and refunds, disputes and chargebacks are resolved with the seller under its terms of trade.
          </p>
        </Section>

        <Section id="module-terms" title="11. Module terms, and chemicals">
          <p>
            11.1 Each Module has its own terms of use (&ldquo;Module Terms&rdquo;), which every User with access to
            that Module accepts in the Platform before first using it, and again when they change. Module Terms
            apply in addition to these Terms and prevail over them for that Module.
          </p>
          <p>
            11.2 In the Spray Module in particular, and without limiting its Module Terms: the Organisation is responsible for chemical
            selection, label and permit compliance, withholding periods, re-entry intervals, maximum residue
            limits, export requirements, spray drift, equipment calibration, operator training and licensing,
            storage, handling and disposal, and for the accuracy of its spray records. Chemical, rate,
            registration, label and weather information on the Platform must be checked against the current
            approved label and permit before use.
          </p>
        </Section>

        <Section id="irrigation" title="12. Irrigation, hardware and remote control">
          <p>
            12.1 The Platform can send commands to, schedule and report on irrigation controllers, pumps,
            valves, relays, sensors, bridges and other devices. Those commands travel over networks, radios,
            the internet, and devices and power supplies we do not control. <strong>We do not guarantee that
            any command, schedule, stop, alert or reading will be delivered, executed, received or reported
            correctly or on time.</strong>
          </p>
          <p>12.2 You are responsible for:</p>
          <Bullets>
            <li>the design, installation, wiring, maintenance and safe operation of your irrigation system and connected equipment, including pumps, pressure protection and electrical work, by appropriately qualified people;</li>
            <li>keeping independent manual controls, isolation and local fail-safes so that water and pumps can be controlled on site without the Platform;</li>
            <li>checking that equipment has actually started or stopped when it matters;</li>
            <li>ensuring anyone near equipment that can be started remotely is protected from unexpected operation; and</li>
            <li>complying with your water entitlements, licences, allocations, restrictions and environmental obligations.</li>
          </Bullets>
          <p>
            12.3 The Platform is not a safety system, an alarm-monitoring service or a substitute for
            supervision. Alerts are provided on a best-efforts basis and may be delayed or not delivered.
          </p>
          <p>
            12.4 Flow, pressure, pump-sizing and application-rate calculations depend on the information you
            enter and on simplified models. They are not an engineering design.
          </p>
          <p>
            12.5 Hardware supplied by us is also subject to any warranty and terms supplied with it. Firmware
            and bridge software may be updated remotely to fix defects, maintain security or add features.
          </p>
        </Section>

        <Section id="forecasts" title="13. Weather, sensors, forecasts and pest models">
          <p>
            13.1 Weather observations, forecasts, modelled estimates, Delta T, evapotranspiration, chill and
            degree-day figures, infection and risk models, maturity and harvest estimates, and sensor
            readings are estimates. Grid-based weather describes an area, not your block. Sensors can drift,
            fail, lose power or lose connection.
          </p>
          <p>
            13.2 A &ldquo;low risk&rdquo;, &ldquo;clear&rdquo; or &ldquo;protected&rdquo; result, or the
            absence of an alert, is not a statement that conditions are safe or that a crop is protected. You
            must use your own observations and advice in making any decision.
          </p>
        </Section>

        <Section id="workforce" title="14. Timesheets, payroll, leave and workforce">
          <p>
            14.1 The Organisation is the employer (or principal) of its workers. It is solely responsible for
            complying with workplace law, including the <em>Fair Work Act 2009</em> (Cth), the National
            Employment Standards, modern awards (including piece-rate requirements under the Horticulture
            Award), enterprise agreements, superannuation, payroll tax, PAYG withholding, workers&rsquo;
            compensation, record-keeping obligations, labour-hire licensing and work health and safety law.
          </p>
          <p>
            14.2 Pay calculations, rate settings, leave accruals and balances, public holidays, rosters,
            labour costs and payroll exports are produced from the settings and records your Organisation
            provides. You must check them before paying anyone or lodging anything. Where your Organisation
            connects a payroll system, that system&rsquo;s figures and rules apply to it.
          </p>
          <p>
            14.3 Location, presence and GPS features record where workers and equipment are. Before enabling
            them, the Organisation must give any notice and obtain any consent required by law (including
            workplace surveillance and privacy laws in the relevant State or Territory) and use the data only
            for lawful purposes.
          </p>
        </Section>

        <Section id="whs" title="15. Work health and safety, and ISO 45001">
          <p>
            15.1 The Platform includes tools for managing work health and safety — hazard and incident reporting,
            investigations, risk assessments, inspections, audits, corrective actions, training and competency
            records, emergency plans, documents, objectives and management reviews — structured with reference to
            ISO 45001 (occupational health and safety management systems). Safety features also appear in other
            Modules, such as equipment inductions and pre-start checks, an induction pack of your Organisation&rsquo;s
            policies for workers to acknowledge, spray PPE and weather limits, and calibration records.
          </p>
          <p>
            15.2 <strong>Using these tools does not make your Organisation compliant with any law or conformant
            with ISO 45001.</strong> ISO 45001 is a voluntary international standard. Conformance depends on how
            your Organisation actually operates, and certification can only be granted by an accredited
            certification body. {COMPANY} is not a certification body and does not certify, audit or endorse any
            Organisation&rsquo;s safety management system.
          </p>
          <p>
            15.3 <strong>An ISO 45001 implementation is not a substitute for your legal obligations.</strong> Work
            health and safety law differs between countries, and within Australia between the Commonwealth and
            each State and Territory — for example, Victoria operates under its own occupational health and
            safety legislation rather than the model WHS laws, and Western Australia has its own WHS Act. Each
            jurisdiction has its own regulations, codes of practice, licensing and high-risk-work requirements,
            plant registration, notification rules and regulator. Industry-specific rules also apply, such as those
            for agricultural chemicals, quad bikes and other farm machinery, and working at heights. Your
            Organisation is responsible for identifying and complying with every law that applies wherever it
            operates, and those duties cannot be transferred to, or shared with, {COMPANY}.
          </p>
          <p>15.4 In particular, your Organisation (and its officers) remain responsible for:</p>
          <Bullets>
            <li>its primary duty of care to workers and others, and officers&rsquo; due-diligence duties;</li>
            <li>consulting with workers and health and safety representatives as the law requires, which recording a consultation on the Platform does not in itself satisfy;</li>
            <li>
              <strong>notifying the regulator of notifiable incidents, immediately and by the means the law
              requires, and preserving the incident site.</strong> Marking an incident as notifiable, or recording a
              regulator&rsquo;s contact details on the Platform, does not notify anyone. {COMPANY} never notifies a
              regulator on your behalf;
            </li>
            <li>verifying licences, tickets, competencies and training records, which the Platform stores but does not check with the issuing body;</li>
            <li>protecting people who raise safety concerns from victimisation or adverse action, and handling anonymous or confidential reports — including any decision to reveal a reporter&rsquo;s identity — lawfully and in line with its own policies; and</li>
            <li>keeping safety records for as long as the law in each jurisdiction requires.</li>
          </Bullets>
          <p>
            15.5 Checklists, templates, risk matrices, hazard lists, emergency-plan and procedure templates, and any
            default settings we provide are generic starting points. They may not reflect the current law, codes of
            practice or standards in your jurisdiction, or the hazards at your workplace. They must be reviewed and
            adapted by a competent person before use.
          </p>
          <p>
            15.6 The Platform is not an emergency service. In an emergency, call 000 (or your local emergency number)
            and follow your emergency procedures; do not rely on a report, alert or notification made through the
            Platform. Reminders and due dates for inspections, training, reviews and corrective actions are
            provided on a best-efforts basis and may not arrive.
          </p>
          <p>
            15.7 Equipment inductions record your Organisation&rsquo;s own judgement that a person is competent to
            operate a machine. When the Platform declines to assign or start a machine for someone with no current
            induction recorded, it is applying the records your Organisation keeps; it does not assess anyone&rsquo;s
            competence, and a sign-off, override or acknowledgement recorded on the Platform does not by itself
            discharge your Organisation&rsquo;s duty to train, instruct and supervise. Your Organisation can switch
            this check off, and is responsible for the content of its inductions and policies.
          </p>
        </Section>

        <Section id="maps" title="16. Maps, boundaries, areas and land data">
          <p>
            16.1 Farm, block and planting boundaries, areas, row and tree counts are drawn or entered by you
            and calculated approximately. They are not a survey and do not establish legal title, boundaries,
            easements or areas.
          </p>
          <p>
            16.2 Property, parcel, zoning and planning information retrieved from public registers is
            provided for convenience and may be incomplete or out of date. It is not a title search or a
            planning certificate.
          </p>
        </Section>

        <Section id="equipment" title="17. Equipment, maintenance and calibration">
          <p>
            17.1 Service intervals, reminders, parts suggestions, pre-start checklists and calibration
            records are tools to help you manage your equipment. They do not replace manufacturer
            instructions, your maintenance program, qualified servicing, road-registration or roadworthiness
            requirements, or your work health and safety obligations.
          </p>
          <p>
            17.2 A reminder that does not arrive, or a parts suggestion that is wrong, does not transfer
            responsibility for the equipment to us. Always confirm parts against the manufacturer&rsquo;s
            specifications.
          </p>
        </Section>

        <Section id="automated" title="18. AI-assisted and automated features">
          <p>
            18.1 Some features use artificial intelligence, web research or automated extraction — for
            example researching a pest or disease, looking up plant variety protection, reading a chemical
            label, or suggesting parts. These features can produce output that is incomplete, out of date
            or simply wrong, while appearing confident.
          </p>
          <p>
            18.2 Output of these features is presented as a proposal for you to review. You are responsible
            for checking it against authoritative sources before saving it, relying on it or sharing it.
            Once you save or rely on it, it is Your Content.
          </p>
          <p>
            18.3 To provide these features, the information you submit may be sent to our AI and search
            service providers, as described in our Privacy Policy. Do not submit information to them that you
            are not permitted to share.
          </p>
        </Section>

        <Section id="integrations" title="19. Third-party services and integrations">
          <p>
            19.1 The Platform can connect to services we do not operate, such as accounting and payroll
            systems, payment providers, mapping, weather and email services, app stores and device
            platforms. Your use of those services is governed by their own terms, and you authorise us to
            exchange data with them as needed to provide the integration you enable.
          </p>
          <p>
            19.2 We are not responsible for third-party services, including their availability, their
            handling of your data, or the effect of data sent to them at your direction (for example
            timesheets, leave or invoices posted to your payroll or accounting system). Check posted data in
            the receiving system.
          </p>
          <p>
            19.3 A third party may change or withdraw its service or its interface, which may cause an
            integration to stop working. We will make reasonable efforts to maintain integrations but do not
            guarantee them.
          </p>
        </Section>

        <Section id="acceptable-use" title="20. Acceptable use">
          <p>You must not, and must not allow anyone else to:</p>
          <Bullets>
            <li>use the Platform in breach of any law, or to infringe anyone&rsquo;s rights;</li>
            <li>enter records you know to be false or misleading, or alter records to mislead an auditor, regulator, certifier, customer, employee or rights holder;</li>
            <li>access, or attempt to access, another Organisation&rsquo;s data, or any part of the Platform you are not authorised to use;</li>
            <li>probe, scan or test the security of the Platform, or circumvent any access control, licence check or rate limit, without our prior written permission;</li>
            <li>copy, scrape, harvest or bulk-extract data from the Platform (other than exporting Your Content through the features we provide), or use it to build a competing product or dataset;</li>
            <li>reverse engineer, decompile or modify the Platform, its apps or firmware, except to the extent the law does not allow this to be restricted;</li>
            <li>upload malicious code, or interfere with the operation, integrity or performance of the Platform or connected devices;</li>
            <li>resell, sublicense or provide the Platform to third parties except as we permit in writing; or</li>
            <li>upload personal information about others without the authority and notices required by law.</li>
          </Bullets>
        </Section>

        <Section id="privacy" title="21. Privacy and personal information">
          <p>
            21.1 We handle personal information in accordance with our{' '}
            <Link to="/privacy" className="text-primary-600 hover:underline">Privacy Policy</Link> and the{' '}
            <em>Privacy Act 1988</em> (Cth).
          </p>
          <p>
            21.2 Each Organisation is responsible for the personal information it collects about its
            employees, contractors, workers, customers and contacts and enters into the Platform, including
            giving notices, obtaining consents and responding to access and correction requests. We process
            that information on the Organisation&rsquo;s behalf to provide the Platform.
          </p>
          <p>
            21.3 If we become aware of a data breach affecting Your Content, we will notify affected
            Organisations and act as required by the Notifiable Data Breaches scheme.
          </p>
          <p>
            21.4 When an Organisation gives a reviewer access or exports a compliance pack under clause 8.5,
            we disclose the records it selects on its instruction. We limit the personal information in them
            to what is needed to show compliance — names, roles, credentials, training and induction
            records — and do not include contact details, pay, dates of birth or health information. An
            Organisation can hide people&rsquo;s names for a reviewer who is not certifying it; a
            certification audit ordinarily needs them, because the reviewer must match each record to the
            person who made it.
          </p>
        </Section>

        <Section id="availability" title="22. Availability, changes and updates">
          <p>
            22.1 We aim to keep the Platform available and reliable, but it is provided on an &ldquo;as
            available&rdquo; basis. It may be interrupted for maintenance, updates, faults, or events outside
            our control, and it relies on internet, mobile and radio connectivity at your locations.
          </p>
          <p>
            22.2 We continually develop the Platform and may add, change or remove features. If we remove a
            feature that is material to a paid Module, we will give reasonable notice and, if you cancel as a
            result, refund the unused portion of Fees you prepaid for that Module.
          </p>
          <p>
            22.3 Mobile apps may update their content automatically. Features marked as beta, preview or
            trial are provided for evaluation and may be less reliable or withdrawn.
          </p>
        </Section>

        <Section id="export" title="23. Data export, retention and deletion">
          <p>
            23.1 You can export Your Content using the export and report features in the Platform. You are
            responsible for keeping your own copies of records you are legally required to retain (for
            example chemical-use, employment, tax, traceability and certification records). The Platform is
            not a certified archive.
          </p>
          <p>
            23.2 We maintain backups for disaster recovery, but backups are not a service for restoring
            individual records you delete or change.
          </p>
          <p>
            23.3 After your subscription ends, we will keep Your Content for at least 90 days so that you can
            reactivate or request an export. After that we may delete it, subject to any retention we are
            required by law to keep, and to residual copies in backups that expire in the ordinary course.
          </p>
          <p>
            23.4 Shared Content that other Organisations have already relied on or incorporated into their
            own records (for example an observation included in a derived statistic) may not be fully
            removable from their records.
          </p>
        </Section>

        <Section id="our-ip" title="24. Our intellectual property and feedback">
          <p>
            24.1 We and our licensors own all rights in the Platform, including its software, firmware,
            designs, models, curated Reference Data, documentation and branding. Subject to these Terms and
            payment of Fees, we grant your Organisation a non-exclusive, non-transferable right for its Users
            to use the Platform for its internal business purposes during its subscription.
          </p>
          <p>
            24.2 If you give us suggestions or feedback, we may use them freely without obligation to you.
          </p>
        </Section>

        <Section id="warranties" title="25. Warranties and the Australian Consumer Law">
          <p>
            25.1 Nothing in these Terms excludes, restricts or modifies any guarantee, right or remedy that
            cannot lawfully be excluded, restricted or modified, including under the Australian Consumer Law
            (&ldquo;Non-excludable Rights&rdquo;).
          </p>
          <p>
            25.2 Subject to the Non-excludable Rights, the Platform, Reference Data, calculations and all
            output are provided &ldquo;as is&rdquo;, and we exclude all warranties, representations and
            conditions not expressly set out in these Terms, including as to accuracy, completeness,
            fitness for a particular purpose, and uninterrupted or error-free operation.
          </p>
          <p>
            25.3 Where we are permitted to limit our liability for failure to comply with a Non-excludable
            Right, our liability is limited, at our option, to supplying the services again or paying the
            cost of having them supplied again.
          </p>
        </Section>

        <Section id="liability" title="26. Limitation of liability">
          <p>25.1 Subject to clause 25.1 and to the maximum extent permitted by law:</p>
          <Bullets>
            <li>
              we are not liable for any indirect or consequential loss, or for any of the following, however
              caused: loss of crop, yield, fruit, trees or plant material; crop damage, residue exceedance or
              contamination; loss of market access, export rejection or certification; water loss, flooding,
              under- or over-watering; damage to equipment; spray drift; animal or environmental harm; loss of
              profit, revenue, business or goodwill; wage or payroll errors and associated penalties; loss or
              corruption of data; or any liability to a third party;
            </li>
            <li>
              we are not liable for loss arising from Your Content, the content or conduct of other
              Organisations, transactions between Organisations, Reference Data, third-party services,
              connectivity or devices outside our control, or decisions made in reliance on the Platform; and
            </li>
            <li>
              our total aggregate liability arising out of or in connection with these Terms or the Platform,
              whether in contract, tort (including negligence), statute or otherwise, is limited to the Fees
              paid by your Organisation to us in the 12 months before the event giving rise to the claim.
            </li>
          </Bullets>
          <p>
            26.2 Our liability is reduced to the extent that any loss was caused or contributed to by you, your
            Users, another Organisation or a third party, or by a failure to take reasonable steps to avoid or
            reduce the loss.
          </p>
          <p>
            26.3 Nothing in this clause limits liability for fraud, or for death or personal injury caused by
            our negligence, to the extent that liability cannot be limited by law.
          </p>
        </Section>

        <Section id="indemnity" title="27. Indemnity">
          <p>
            27.1 Your Organisation indemnifies us, our related bodies corporate and our officers, employees and
            contractors against any loss, damage, cost (including reasonable legal costs) or liability arising
            from a claim by a third party to the extent that it arises from:
          </p>
          <Bullets>
            <li>Your Content, including any claim that it infringes a Plant Breeder&rsquo;s Right, patent, trade mark, copyright or contractual right;</li>
            <li>goods or services your Organisation supplies to, or acquires from, any other person, including through the Platform;</li>
            <li>your Organisation&rsquo;s breach of these Terms or of any law; or</li>
            <li>the activities your Organisation carries out or controls, including spraying, irrigation, employment and the operation of equipment.</li>
          </Bullets>
          <p>
            27.2 The indemnity is reduced to the extent the loss was caused by our own negligence, breach or
            wilful misconduct.
          </p>
        </Section>

        <Section id="termination" title="28. Suspension and termination">
          <p>
            28.1 You may stop using the Platform and cancel your subscription at any time in accordance with
            clause 5.
          </p>
          <p>27.2 We may suspend or terminate access for an Organisation or a User if:</p>
          <Bullets>
            <li>they materially breach these Terms and do not remedy the breach within 14 days of notice (or immediately, if the breach cannot be remedied);</li>
            <li>Fees remain unpaid after notice under clause 5.4;</li>
            <li>it is reasonably necessary to protect the security or integrity of the Platform, other Organisations or any person; or</li>
            <li>we are required to by law.</li>
          </Bullets>
          <p>
            28.3 We may stop offering the Platform or a Module entirely by giving at least 90 days&rsquo; notice
            and refunding prepaid Fees for the period after it ends.
          </p>
          <p>
            28.4 Clauses 6 to 10, 15, 23 and 25 to 32, and any accrued rights, survive the end of these Terms.
          </p>
        </Section>

        <Section id="changes" title="29. Changes to these Terms">
          <p>
            29.1 We may update these Terms to reflect changes to the Platform, the law or our business. We will
            give Organisation Admins at least 30 days&rsquo; notice of a change that materially reduces your
            rights or increases your obligations, through the Platform or by email.
          </p>
          <p>
            29.2 If you do not accept a material change, you may cancel before it takes effect and we will
            refund prepaid Fees for the period after cancellation. Continuing to use the Platform after the
            change takes effect means you accept it.
          </p>
          <p>
            29.3 The effective date and version at the top of this page show which version currently applies.
          </p>
        </Section>

        <Section id="disputes" title="30. Disputes and governing law">
          <p>
            30.1 If a dispute arises, the parties will first try in good faith to resolve it by negotiation
            between senior representatives for at least 30 days before starting proceedings, except where
            urgent interlocutory relief is needed.
          </p>
          <p>
            30.2 These Terms are governed by the laws of Victoria, Australia. Each party submits to the
            non-exclusive jurisdiction of the courts of Victoria and the courts that may hear appeals from them.
          </p>
          <p>
            30.3 Disputes between Organisations — for example about an order, a licence or a variety — are for
            those Organisations to resolve. We may, at our discretion, provide records held on the Platform
            where both parties consent or where the law requires it.
          </p>
        </Section>

        <Section id="general" title="31. General">
          <Bullets>
            <li><strong>Order of precedence.</strong> If there is inconsistency, the following apply in this order: a written agreement signed by both parties; Tree Sales Terms and any cultivar licence (for that sale or cultivar); Module-specific terms (for that Module); an order form, quote or written billing terms; these Terms; the Privacy Policy.</li>
            <li><strong>Force majeure.</strong> Neither party is liable for delay or failure caused by events beyond its reasonable control, including power, network or cloud-provider outages, natural disasters, fire, flood, pandemic or government action. This does not excuse payment of Fees.</li>
            <li><strong>Assignment.</strong> You may not assign these Terms without our consent, which will not be unreasonably withheld. We may assign them to a successor to our business on notice to you.</li>
            <li><strong>Notices.</strong> We may give notice by email to Organisation Admins or through the Platform. You may give notice to <a href={`mailto:${CONTACT_EMAIL}`} className="text-primary-600 hover:underline">{CONTACT_EMAIL}</a>.</li>
            <li><strong>Severability.</strong> If any provision is invalid or unenforceable, it is read down or severed to the extent necessary, and the rest continues in effect.</li>
            <li><strong>Waiver.</strong> A failure or delay in exercising a right is not a waiver of it.</li>
            <li><strong>Relationship.</strong> Nothing in these Terms creates a partnership, joint venture, agency or employment relationship.</li>
            <li><strong>Entire agreement.</strong> These Terms, together with the documents referred to in them, are the entire agreement about their subject matter.</li>
          </Bullets>
        </Section>

        <Section id="contact" title="32. Contact us">
          <p>
            Questions about these Terms, notices, and intellectual property complaints can be sent to{' '}
            <a href={`mailto:${CONTACT_EMAIL}`} className="text-primary-600 hover:underline">{CONTACT_EMAIL}</a>.
          </p>
        </Section>
      </main>

      {/* Footer */}
      <div className="mt-16">
        <PublicFooter />
      </div>
    </div>
  );
}

export default TermsAndConditions;
